import { View } from '../../primitives/View';
import { DARK_LIFTED } from '../../styles/elevation';
import { cn } from '../../utils/cn';
import { restyleKey } from '../../utils/restyleKey';
import { Badge } from '../Badge';
import type { CardBadge } from './CardBadge';
import { CardChipTooltip } from './CardChipTooltip';
import { TodoChip } from './CardTodoBadge';
import { visibleCardChips } from './visibleCardChips';

/**
 * The activity-chip row shared by every DS card (DeckCard / FolderCard /
 * StudyGroupCard) — `glass`-style Badges (translucent fill + thin border)
 * on the card's glass body panel. Chips with `count === 0` are hidden; both views render
 * the compact size-`xs` `icon + count` chip. The pluralized `label` is the
 * a11y name AND (web) the chip's hover-tooltip text.
 *
 * When EVERY chip is hidden and the caller passed `emptyLabel`, the row
 * shows a single label-only badge instead ("no study material yet") — unless
 * there's a to-do chip, which is itself study material to show. A set that
 * holds files shows its files chip, so it never reads as empty.
 *
 * `todoCount` adds the leading "To-do: N" chip on LIST rows (grid cards carry
 * that chip in their corner instead) and promotes the flashcards chip to the
 * primary palette in BOTH views — Figma 5868:74033.
 *
 * The row is ALWAYS a single line, in both views. Chips keep their natural
 * size — `flex-wrap` (not nowrap) so an overflowing chip wraps instead of
 * shrinking (which would crush the icon into an empty pill) — and the row is
 * capped at one chip's height with `overflow-hidden`, so wrapped chips are
 * simply HIDDEN, never rendered on a second line. `relative` keeps the row
 * above the grid body's absolutely-positioned panel.
 *
 * Dark mode: the neutral chips take the elevation ladder's NESTED step
 * (`DARK_LIFTED`, one clear step above the card surface) — the Badge's own
 * glass fill is the surface colour itself, so on a DARK_LAYER1 card it read as
 * a hole, darker than the orange-lit glass panel around it.
 *
 * The row ALWAYS renders (min-h = one chip's height) even with nothing to
 * show — an empty card would otherwise let the title + ⋯ menu drop into the
 * freed space, so cards with and without activity wouldn't line up.
 */
export function CardChips({
  badges,
  emptyLabel,
  view,
  badgeSize,
  pinned = false,
  todoCount,
  todoLabel,
  todoTooltip,
}: {
  badges: CardBadge[] | undefined;
  /** Single fallback badge shown when every chip's count is 0. */
  emptyLabel?: string;
  view: 'grid' | 'list';
  /**
   * Chip size — `xs` (24px) or `s` (28px). Defaults per DS: list rows use
   * `s`, grid tiles `xs` (the expanded deck tile overrides to `s`).
   */
  badgeSize?: 'xs' | 's';
  /**
   * Freeze the row (and its chips) at the DESIGN height instead of letting
   * the tablet rem ramp grow it — set by the compact grid tile, whose panel
   * is a fixed 89/98px and therefore has no room to give. Without it a chip
   * row that grew from 24 to 30 on an iPad pushed up into the title.
   *
   * The chips' own type still scales; only the pill's box is held, which is
   * what the card's fixed anatomy actually depends on.
   */
  pinned?: boolean;
  /**
   * Flashcards waiting to be studied. Above `0` the LIST row leads with the
   * "To-do" chip (grid cards carry it in their top-right corner instead — see
   * `CardTodoBadge`), and BOTH views promote the flashcards chip to primary.
   */
  todoCount?: number;
  /** Localized to-do text ("To-do: 18"); falls back to the bare count. */
  todoLabel?: string;
  todoTooltip?: string;
}) {
  const size = badgeSize ?? (view === 'list' ? 's' : 'xs');
  const hasTodo = !!todoCount;
  // Which chips show, and which one a to-do promotes: `visibleCardChips`.
  const visible = visibleCardChips(badges, hasTodo);
  // min-h reserves one chip's height even when empty; max-h + overflow-hidden
  // clip anything that wrapped past the single visible line (see doc above).
  const oneLine = pinned
    ? 'min-h-6 max-h-6 native:min-h-[24px] native:max-h-[24px]'
    : size === 's'
      ? 'min-h-7 max-h-7'
      : 'min-h-6 max-h-6';
  // The chips take Badge's own `pinned` (box + type); the row's cap above
  // matches it so a pinned chip can't be clipped by an unpinned row.
  return (
    <View
      className={cn(
        'relative w-full flex-row flex-wrap items-center gap-1 overflow-hidden',
        oneLine,
      )}>
      {/* List rows lead with the to-do chip (Figma 5868:74056); grid cards
          carry it in their corner instead, so it must not double up here. */}
      {view === 'list' && todoCount ? (
        <TodoChip count={todoCount} label={todoLabel} tooltip={todoTooltip} />
      ) : null}
      {visible.length === 0 && emptyLabel && !hasTodo ? (
        <Badge
          type="label"
          style="glass"
          size={size}
          colour="neutral"
          pinned={pinned}
          className={DARK_LIFTED}>
          {emptyLabel}
        </Badge>
      ) : (
        visible.map((badge, i) => {
          const colour = badge.colour ?? 'neutral';
          return (
            // `colour` in the key: a to-do promotes the flashcards chip to
            // primary, and that flip must REMOUNT the chip rather than swap its
            // alpha class in place (the NativeWind restyle crash — `restyleKey`).
            <CardChipTooltip
              key={restyleKey(`${badge.label ?? badge.count}-${i}`, colour)}
              label={badge.label}>
              <Badge
                type={badge.icon ? 'icon-label' : 'label'}
                icon={badge.icon}
                style="glass"
                size={size}
                colour={colour}
                pinned={pinned}
                className={colour === 'neutral' ? DARK_LIFTED : undefined}
                accessibilityLabel={badge.label}>
                {String(badge.count)}
              </Badge>
            </CardChipTooltip>
          );
        })
      )}
    </View>
  );
}
